import { useCallback, useEffect, useMemo, useState } from 'react';

// Values shown on screen, and only for a while: each hides itself a minute after it was revealed, and all of them as
// soon as the tab is hidden. They live in this hook's state, never in the query cache or the browser's storage.
export const HIDE_AFTER_MS = 60_000;

export interface Revealed {
    get: (id: string) => string | undefined;
    // When it hides itself, for the line that runs out under it.
    until: (id: string) => number | undefined;
    show: (values: Record<string, string>) => void;
    hide: (id: string) => void;
    clear: () => void;
    count: number;
}

export function useRevealed(): Revealed {
    const [values, setValues] = useState<ReadonlyMap<string, { value: string; until: number }>>(new Map());

    useEffect(() => {
        if (values.size === 0) {
            return;
        }
        const next = Math.min(...[...values.values()].map((entry) => entry.until));
        const timer = setTimeout(
            () => {
                const now = Date.now();
                setValues((current) => new Map([...current].filter(([, entry]) => entry.until > now)));
            },
            Math.max(0, next - Date.now()) + 20,
        );

        return () => clearTimeout(timer);
    }, [values]);

    useEffect(() => {
        const onVisibility = (): void => {
            if (document.visibilityState === 'hidden') {
                setValues(new Map());
            }
        };
        document.addEventListener('visibilitychange', onVisibility);

        return () => document.removeEventListener('visibilitychange', onVisibility);
    }, []);

    const show = useCallback((shown: Record<string, string>) => {
        const until = Date.now() + HIDE_AFTER_MS;
        setValues((current) => {
            const next = new Map(current);
            for (const [id, value] of Object.entries(shown)) {
                next.set(id, { value, until });
            }

            return next;
        });
    }, []);
    const hide = useCallback((id: string) => {
        setValues((current) => {
            const next = new Map(current);
            next.delete(id);

            return next;
        });
    }, []);
    const clear = useCallback(() => setValues(new Map()), []);

    return useMemo(
        () => ({
            get: (id: string) => values.get(id)?.value,
            until: (id: string) => values.get(id)?.until,
            show,
            hide,
            clear,
            count: values.size,
        }),
        [values, show, hide, clear],
    );
}
